import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getSurveyRecord,
    updateSurveyRecordResponse,
} from '@/modules/Survey/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Calendar, Lock, Pencil, Plus, Ticket, User } from 'lucide-react'

dayjs.extend(utc)
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import SubHeaders from '@/components/Headers/SubHeaders'
import RecordResponseDialog from '../../dialogs/RecordResponseDialog'
import {
    RecordResponse,
    UpdateSurveyRecordResponsePayload,
} from '../../../types'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { SurveyRecordTable } from './components/SurveyRecordTable'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const SurveyRecordDetail = () => {
    const { t } = useTranslation('surveys')
    const { recordId } = useParams()

    const { data: record, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SURVEYS, 'detail', recordId],
        queryFn: () => getSurveyRecord(recordId!),
        enabled: !!recordId,
    })

    const { mutateAsync: updateMutation } = useMutationWithHandler(
        {
            mutationFn: ({
                surveyRecordId,
                payload,
            }: {
                surveyRecordId: number
                payload: UpdateSurveyRecordResponsePayload
            }) => updateSurveyRecordResponse(surveyRecordId, payload),
        },
        {
            reactQueryKeys: [QUERY_KEYS.SURVEYS],

            onSuccess: () => {
                toast.success(t('surveys.edit_success', 'Saved successfully'))
                setIsDialogOpen(false)
            },
            onError: () => {
                toast.error(t('surveys.edit_error', 'Failed to save'))
            },
        }
    )

    const [isDialogOpen, setIsDialogOpen] = useState(false)

    const handleSave = async (newResponses: RecordResponse) => {
        if (!record) return

        const payload: UpdateSurveyRecordResponsePayload = {
            score_service_quality: newResponses.score_service_quality,
            score_customer_care: newResponses.score_customer_care,
            score_tech_support: newResponses.score_tech_support,
            score_issue_resolution: newResponses.score_issue_resolution,
            score_referral_willingness: newResponses.score_referral_willingness,
            note_general: newResponses.note_general,
            note_service_quality: newResponses.note_service_quality,
            note_customer_care: newResponses.note_customer_care,
            note_tech_support: newResponses.note_tech_support,
            note_issue_resolution: newResponses.note_issue_resolution,
            note_referral_willingness: newResponses.note_referral_willingness,
            improvement: newResponses.improvement,
            upsale_potential: newResponses.upsale_potential,
        }

        try {
            await updateMutation({
                surveyRecordId: record.survey_record_id,
                payload,
            })
        } catch {
            toast.error(t('surveys.edit_error', 'Failed to save'))
        }
    }

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-[200px] w-full" />
                <Skeleton className="h-[300px] w-full" />
            </div>
        )
    }

    if (!record) {
        return <div>{t('common.not_found', 'Record not found')}</div>
    }

    const initialResponses: RecordResponse = {
        score_service_quality: record.score_service_quality,
        score_customer_care: record.score_customer_care,
        score_tech_support: record.score_tech_support,
        score_issue_resolution: record.score_issue_resolution,
        score_referral_willingness: record.score_referral_willingness,
        note_general: record.note_general || '',
        note_service_quality: record.note_service_quality || '',
        note_customer_care: record.note_customer_care || '',
        note_tech_support: record.note_tech_support || '',
        note_issue_resolution: record.note_issue_resolution || '',
        note_referral_willingness: record.note_referral_willingness || '',
        improvement: record.improvement || '',
        upsale_potential: record.upsale_potential || [],
    }

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    { title: t('surveys.title'), path: paths.surveys },
                    {
                        title: record.survey_record_id.toString(),
                        path: generatePathDatacentral(
                            paths.surveyRecordDetail,
                            {
                                surveyRecordId:
                                    record.survey_record_id.toString(),
                            }
                        ),
                    },
                ]}
            />

            <RecordResponseDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                customerName={record.customer_name}
                initialResponses={initialResponses}
                onSave={handleSave}
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
                <div className="lg:col-span-2 space-y-2">
                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-3">
                                        <CardTitle>
                                            {t(
                                                'surveys.record.title',
                                                'Survey Record Details'
                                            )}
                                        </CardTitle>
                                        <Badge variant="outline">
                                            #{record.survey_record_id}
                                        </Badge>
                                    </div>
                                </div>
                                {record.is_locked ? (
                                    <Badge
                                        variant="outline"
                                        className="gap-2 text-gray-500"
                                    >
                                        <Lock className="h-3.5 w-3.5" />
                                        {t('surveys.quarter_locked')}
                                    </Badge>
                                ) : (
                                    <Button
                                        onClick={() => setIsDialogOpen(true)}
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                    >
                                        <Pencil className="h-4 w-4" />
                                        {t('surveys.edit')}
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-blue-100 rounded-full">
                                        <User className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">
                                            {t(
                                                'surveys.column.customer_name',
                                                'Customer'
                                            )}
                                        </p>
                                        <p className="font-medium">
                                            {record.customer_name}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-orange-100 rounded-full">
                                        <User className="h-5 w-5 text-orange-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">
                                            {t(
                                                'surveys.column.done_by',
                                                'Surveyor'
                                            )}
                                        </p>
                                        <p className="font-medium">
                                            {record.employee_name}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-green-100 rounded-full">
                                        <Calendar className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">
                                            {t(
                                                'surveys.column.created_at',
                                                'Created At'
                                            )}
                                        </p>
                                        <p className="font-medium">
                                            {dayjs
                                                .utc(record.created_at)
                                                .local()
                                                .format('DD/MM/YYYY HH:mm')}
                                        </p>
                                    </div>
                                </div>
                                {record.updated_at && (
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-purple-100 rounded-full">
                                            <Calendar className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">
                                                {t(
                                                    'surveys.column.updated_at',
                                                    'Updated At'
                                                )}
                                            </p>
                                            <p className="font-medium">
                                                {dayjs
                                                    .utc(record.updated_at)
                                                    .local()
                                                    .format('DD/MM/YYYY HH:mm')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <SurveyRecordTable surveyRecord={record} />
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-lg font-semibold flex items-center gap-2">
                                {t('surveys.tickets.title', 'Linked Tickets')}
                                <Badge
                                    variant="secondary"
                                    className="text-xs font-normal"
                                >
                                    {t(
                                        'surveys.tickets.coming_soon',
                                        'Coming Soon'
                                    )}
                                </Badge>
                            </CardTitle>
                            <Ticket className="h-5 w-5 text-muted-foreground" />
                        </CardHeader>
                        <CardContent className="space-y-4 pt-4">
                            <Button className="w-full">
                                <Plus className="mr-2 h-4 w-4" />
                                {t('surveys.tickets.add', 'Create Ticket')}
                            </Button>

                            <div className="space-y-3">
                                {[
                                    {
                                        id: 'TKT-2024-001',
                                        title: 'Follow up on negative feedback',
                                        status: 'In Progress',
                                        priority: 'high',
                                    },
                                    {
                                        id: 'TKT-2024-005',
                                        title: 'Service pricing consultation',
                                        status: 'Open',
                                        priority: 'medium',
                                    },
                                ].map((ticket) => (
                                    <div
                                        key={ticket.id}
                                        className="flex flex-col space-y-2 p-3 border rounded-lg hover:bg-muted/50 transition-colors bg-card text-card-foreground shadow-sm"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-semibold text-sm">
                                                {ticket.id}
                                            </span>
                                            <Badge
                                                variant={
                                                    ticket.status === 'Open'
                                                        ? 'secondary'
                                                        : 'default'
                                                }
                                                className="text-xs"
                                            >
                                                {ticket.status}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground line-clamp-2">
                                            {ticket.title}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default SurveyRecordDetail
